import React from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { RestrictedByCapability } from "../../auth/RestrictedByCapability.js";
import { useUserData } from "../../auth/useUserData.js";
import type { PageTopbarItem } from "../../commons/layout/Page.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { IntegrationsOverview } from "./IntegrationsOverview.js";
import { IntegrationDashboardPage } from "./IntegrationDashboardPage.js";
import { IntegrationSettingsPage } from "./IntegrationSettingsPage.js";
import { getIntegrationMeta } from "./integrationRegistry.js";

const EMPTY_SUBMENU: PageTopbarItem[] = [];

/**
 * Index of /organisation/integrations: the module overview for orgs with the
 * INTEGRATIONS capability. Orgs without it keep the previous index behavior —
 * a redirect to the Websites page when WEBSHOPCMS applies (deep-link
 * compatibility) — and everyone else sees restricted access.
 */
export const IntegrationsIndex = () => {
  const { hasCapability } = useUserData();

  if (!hasCapability("integrations") && hasCapability("webshopcms")) {
    return <Navigate to="websites" replace />;
  }

  return (
    <RestrictedByCapability capability="integrations">
      <IntegrationsOverview submenuItems={EMPTY_SUBMENU} />
    </RestrictedByCapability>
  );
};

/**
 * Routes below /organisation/integrations/:integrationId — dashboard and
 * settings pages. Hidden integrations (e.g. the legacy `pds` v1) have no
 * routes at all.
 */
export const IntegrationRoutes = () => {
  const { integrationId } = useParams();
  if (!integrationId) throw new Error("integrationId is missing");

  if (getIntegrationMeta(integrationId).hidden) {
    return <PageNotFound />;
  }

  return (
    <Routes>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<IntegrationDashboardPage />} />
      <Route path="settings" element={<IntegrationSettingsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};
